import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Text,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  type AwardCriterionDto,
  type AwardSubCriterionDto,
  WeightType,
} from "../../../autogen/bff-api";
import { TipTap } from "../../input/TipTap/TipTap";
import { FormSectionBand } from "./FormSectionBand";

// t('Title') t('Criterion name') t('Title is required') t('Description') t('optional')
// t('Type') t('Choose a type') t('Type is required') t('Weight') t('Options')

export interface AwardCriterionFormValues {
  type: AwardCriterionType | "";
  name: string;
  description: string;
  weight: string;
}

// A reusable library award criterion is a quality or environment criterion; the § 5c exemption handling
// is a procurement-level concern assigned when the criterion is applied to an event.
export const AWARD_LIBRARY_TYPES: AwardCriterionType[] = [
  AwardCriterionType.Quality,
  AwardCriterionType.QualityEnvironmentalCriteria,
];

interface Props {
  /** Lets a button outside the form submit it, so the same fields work in a modal and a tab. */
  formId: string;
  defaultValues?: AwardCriterionFormValues;
  /**
   * Sub-criteria are authored in the wizard, not here, but they must survive an edit through this form —
   * the criterion is rebuilt from the form values, so anything not carried here is dropped. Pass the
   * criterion's existing sub-criteria and they are handed straight back.
   */
  subCriteria?: AwardSubCriterionDto[];
  onSubmit: (values: AwardCriterionDto) => Promise<void> | void;
  /** The library restricts to quality/environment; a template's award model also allows price/cost. */
  allowedTypes?: AwardCriterionType[];
  /** Rendered inside the form, under the fields — where the "save to the library" choice belongs. */
  children?: React.ReactNode;
}

const emptyDefaults: AwardCriterionFormValues = { type: "", name: "", description: "", weight: "" };

/** The one definition of what an award criterion asks for, shared by the library modal and the tab. */
export const AwardCriterionFields = ({
  formId,
  defaultValues,
  subCriteria,
  onSubmit,
  allowedTypes,
  children,
}: Props) => {
  const { t } = useTranslation();
  const types = allowedTypes ?? AWARD_LIBRARY_TYPES;
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AwardCriterionFormValues>({ defaultValues: defaultValues ?? emptyDefaults });

  const submitHandler = handleSubmit(async (values) => {
    if (!values.type) return;
    const parsedWeight = values.weight.trim() === "" ? null : Number(values.weight);
    const weight =
      parsedWeight != null && Number.isFinite(parsedWeight)
        ? { number: parsedWeight, type: WeightType.Percentage }
        : undefined;
    await onSubmit({
      type: values.type,
      name: values.name,
      description: values.description,
      weight,
      subCriteria: subCriteria ?? [],
    });
  });

  return (
    <form id={formId} data-testid="award-criterion-form" onSubmit={submitHandler}>
      {/* The title is what the library is searched on, so every requirement gets one — including a
          criterion inherited from an event that was left unnamed, which is named on its way through
          this form rather than staying unfindable. */}
      <FormControl mb="6" isRequired isInvalid={errors.name !== undefined}>
        <FormLabel>{t("Title")}</FormLabel>
        <Input
          autoFocus
          autoComplete="off"
          data-1p-ignore
          placeholder={t("Criterion name") ?? ""}
          {...register("name", { required: t("Title is required") ?? "" })}
        />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mb="6">
        <FormLabel>
          {t("Description")}{" "}
          <Text as="span" color="smMuted" fontWeight="normal">
            {t("optional")}
          </Text>
        </FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TipTap
              content={value}
              onUpdate={onChange}
              editable={true}
              toolbar={{
                underline: false,
                headings: false,
                colors: false,
                link: false,
                table: false,
                image: false,
                template: false,
              }}
              debounceWait={0}
            />
          )}
        />
      </FormControl>

      <Flex gap="4" mb="6" align="flex-start">
        <FormControl isRequired isInvalid={errors.type !== undefined} flex="1">
          <FormLabel>{t("Type")}</FormLabel>
          <Select
            {...register("type", { required: t("Type is required") ?? "" })}
            borderColor={errors.type ? "smError" : "smBorder"}
          >
            <option value="">{t("Choose a type")}</option>
            {types.map((type) => (
              <option key={type} value={type}>
                {t(type)}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
        </FormControl>

        <FormControl maxW="32">
          <FormLabel>{t("Weight")}</FormLabel>
          <InputGroup>
            <Input type="number" min={0} max={100} placeholder="0" {...register("weight")} />
            <InputRightAddon>%</InputRightAddon>
          </InputGroup>
        </FormControl>
      </Flex>

      {/* The band only earns its place when there is a choice under it — the library modal passes none. */}
      {children && (
        <>
          <FormSectionBand>{t("Options")}</FormSectionBand>
          {children}
        </>
      )}
    </form>
  );
};
